import { Controller } from "@hotwired/stimulus"
import { apiHeaders } from "./sweeper_shared"

export default class extends Controller {
  static values = {
    initialView: String,
    matrixUrl: String,
    prereqsUrl: String,
    activityUrl: String,
    roomsUrl: String,
    programmaUrl: String,
    legendUrl: String
  }

  connect() {
    this.sessions = []
    const route = this.routeState
    this.view = route.view || this.initialViewValue || "matrix"
    this.currentEditorSessionId = route.session || document.getElementById("editor-content")?.dataset.sessionId || ""
    this.loadSessions().then(() => {
      this.updateSessionCount()
      this.switchView(this.view, { replaceHistory: true, reload: false })
      this.element.classList.remove("sweeper-booting")
    })
    this.onSessionUpdated = this.handleSessionUpdated.bind(this)
    this.onSessionDeleted = this.handleSessionDeleted.bind(this)
    this.onEditorSessionSelected = this.handleEditorSessionSelected.bind(this)
    document.addEventListener("sweeper:session-updated", this.onSessionUpdated)
    document.addEventListener("sweeper:session-deleted", this.onSessionDeleted)
    document.addEventListener("sweeper:editor-session-selected", this.onEditorSessionSelected)
    window.addEventListener("popstate", () => this.switchView(this.routeState.view, { writeHistory: false }))
  }

  disconnect() {
    document.removeEventListener("sweeper:session-updated", this.onSessionUpdated)
    document.removeEventListener("sweeper:session-deleted", this.onSessionDeleted)
    document.removeEventListener("sweeper:editor-session-selected", this.onEditorSessionSelected)
  }

  tab(event) {
    const tab = event.target.closest("[data-tab]")
    if (!tab) return

    this.switchView(tab.dataset.tab)
  }

  exportSessions() {
    const data = JSON.stringify(this.sessions, null, 2)
    const blob = new Blob([data], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "klantendag-sweeper-export.json"
    a.click()
    URL.revokeObjectURL(url)
  }

  async switchView(view, options = {}) {
    this.view = view
    const highlightedTab = this.highlightedTabFor(view)
    this.element.querySelectorAll("[data-tab]").forEach(tab => {
      tab.classList.toggle("active", tab.dataset.tab === highlightedTab)
    })
    this.frames.forEach(frame => {
      frame.classList.toggle("hidden", frame.id !== `view-${view}`)
    })

    if (options.reload !== false) await this.loadView(view)
    if (options.writeHistory !== false) this.writeRouteState({ replace: options.replaceHistory === true })
  }

  async loadView(view) {
    if (view === "matrix") return this.reloadFrame("view-matrix", this.matrixUrlValue)
    if (view === "editor") return document.dispatchEvent(new CustomEvent("sweeper:editor-activated"))
    if (view === "prereqs") return this.reloadFrame("view-prereqs", this.prereqsUrlValue)
    if (view === "activity") return this.reloadFrame("view-activity", this.activityUrlValue)
    if (view === "rooms") return document.dispatchEvent(new CustomEvent("sweeper:rooms-activated"))
    if (view === "programma") return document.dispatchEvent(new CustomEvent("sweeper:programma-activated"))
    if (view === "legend") return this.reloadFrame("view-legend", this.legendUrlValue)
  }

  reloadFrame(id, url) {
    const frame = document.getElementById(id)
    if (!frame) return
    if (frame.getAttribute("src") === url && typeof frame.reload === "function") frame.reload()
    else frame.setAttribute("src", url)
  }

  async loadSessions() {
    const response = await fetch("/api/v1/sessions", { credentials: "include", headers: apiHeaders() })
    this.sessions = response.ok ? await response.json() : []
  }

  handleSessionUpdated(event) {
    const session = event.detail?.session
    if (!session?.id) return

    const index = this.sessions.findIndex(existing => existing.id === session.id)
    if (index >= 0) this.sessions[index] = session
    else this.sessions.push(session)
    this.updateSessionCount()
  }

  handleSessionDeleted(event) {
    const id = event.detail?.id
    if (!id) return

    this.sessions = this.sessions.filter(session => session.id !== id)
    this.updateSessionCount()
  }

  handleEditorSessionSelected(event) {
    this.currentEditorSessionId = event.detail?.sessionId || ""
    if (this.view === "editor") this.writeRouteState()
  }

  updateSessionCount() {
    const count = this.sessions.length
    const badge = document.getElementById("tab-badge-matrix")
    if (badge) badge.textContent = count
  }

  writeRouteState({ replace = false } = {}) {
    const params = new URLSearchParams()
    const pathView = this.pathView
    const currentParams = new URLSearchParams(window.location.search)

    if (this.view === "programma" && pathView === "programma" && ["schedule", "timetable"].includes(currentParams.get("view"))) {
      params.set("view", currentParams.get("view"))
    } else if (this.view === "rooms" && pathView === "rooms" && currentParams.get("room")) {
      params.set("room", currentParams.get("room"))
    } else if (this.view !== "matrix" && pathView !== this.view) {
      params.set("view", this.view)
    }

    const editorSession = this.currentEditorSessionId || document.getElementById("editor-content")?.dataset.sessionId
    if (this.view === "editor" && editorSession && this.pathForView(this.view, editorSession) !== `/sweeper/editor/${encodeURIComponent(editorSession)}`) {
      params.set("session", editorSession)
    }

    const query = params.toString()
    const nextPath = this.pathForView(this.view, editorSession)
    const nextUrl = `${nextPath}${query ? `?${query}` : ""}`
    if (nextUrl === `${window.location.pathname}${window.location.search}`) return

    const method = replace ? "replaceState" : "pushState"
    window.history[method]({ view: this.view, session: editorSession }, "", nextUrl)
  }

  get routeState() {
    const params = new URLSearchParams(window.location.search)
    const pathView = this.pathView
    const queryView = params.get("view")
    const valid = new Set(["matrix", "editor", "prereqs", "activity", "rooms", "programma", "legend"])
    const validProgrammaView = new Set(["schedule", "timetable"])
    const session = params.get("session") || this.pathEditorSession
    const room = params.get("room")
    const view = valid.has(queryView) ? queryView : validProgrammaView.has(queryView) && pathView === "programma" ? "programma" : valid.has(pathView) ? pathView : "matrix"
    return { view: session && view === "matrix" ? "editor" : view, session, room }
  }

  get pathView() {
    if (this.pathEditorSession) return "editor"

    return {
      "/rooms": "rooms",
      "/sweeper/rooms": "rooms",
      "/programma": "programma",
      "/sweeper/programma": "programma",
      "/sweeper/prereqs": "prereqs",
      "/sweeper/activity": "activity",
      "/sweeper/legend": "legend"
    }[window.location.pathname]
  }

  get pathEditorSession() {
    return window.location.pathname.match(/^\/sweeper\/editor\/([^/]+)$/)?.[1]
  }

  pathForView(view, editorSession) {
    if (view === "matrix") return "/sweeper"
    if (view === "editor" && editorSession) return `/sweeper/editor/${encodeURIComponent(editorSession)}`
    if (view === "programma") return "/sweeper/programma"
    if (view === "rooms") return "/sweeper/rooms"
    if (view === "prereqs") return "/sweeper/prereqs"
    if (view === "activity") return "/sweeper/activity"
    if (view === "legend") return "/sweeper/legend"

    return window.location.pathname
  }

  highlightedTabFor(view) {
    return view === "editor" ? "matrix" : view
  }

  get frames() {
    return Array.from(this.element.querySelectorAll("turbo-frame[id^='view-']"))
  }
}
